'use client'

import { FormEvent, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

import { stegaClean } from '@sanity/client/stega'
import { PortableTextBlock } from 'next-sanity'

import { sendContactEmail } from '@/app/actions/sendContactEmail'
import { Button, buttonSizeClasses } from '@/app/components/Button'
import { Input, Textarea } from '@/app/components/Form'
import { Icon } from '@/app/components/Icon'
import PortableTextMinimal from '@/app/components/PortableTextMinimal'
import { isProductionDomain, ReCaptchaProvider } from '@/app/components/ReCaptchaProvider'
import { TRACKING_LOCATIONS, TrackingLocation } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import { ContactForm as ContactFormRaw } from '@/sanity.types'
import { dataAttr } from '@/sanity/lib/utils'

export type ContactFormProps = {
  contactForm: ContactFormRaw & { _key?: string }
  tracking: TrackingLocation
  className?: string
  documentId?: string
  documentType?: string
  parentPath?: string
}

/**
 * Internal form component that uses the reCAPTCHA hook.
 * Must be wrapped with ReCaptchaProvider.
 */
const ContactFormInner = ({
  contactForm,
  tracking,
  className,
  documentId,
  documentType,
  parentPath,
}: ContactFormProps) => {
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const thankYouMessage =
    stegaClean(contactForm.thankYouMessage) ||
    "We've received your message and our support team will be in touch soon."

  const attr =
    contactForm._key && documentId && documentType && parentPath
      ? dataAttr({
          id: documentId,
          type: documentType,
          path: `${parentPath}[_key=="${contactForm._key}"]`,
        })
      : undefined

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)
    setErrors({})

    const formData = new FormData(e.currentTarget)
    const data = {
      name: formData.get('name') as string,
      email: formData.get('email') as string,
      issueType: formData.get('issueType') as string,
      message: formData.get('message') as string,
    }

    // Basic validation
    const newErrors: Record<string, string> = {}
    if (!data.name.trim()) newErrors.name = 'Name is required'
    if (!data.email.trim()) newErrors.email = 'Email is required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      newErrors.email = 'Please enter a valid email address'
    }
    if (!data.issueType.trim()) newErrors.issueType = 'Issue type is required'
    if (!data.message.trim()) newErrors.message = 'Message is required'

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      setIsSubmitting(false)
      return
    }

    try {
      const recipient = stegaClean(contactForm.recipient) || 'support@kitemobile.com'

      // Generate reCAPTCHA token only on production domain
      let recaptchaToken = ''
      if (isProductionDomain()) {
        if (!executeRecaptcha) {
          setErrors({ submit: 'reCAPTCHA not loaded. Please try again.' })
          setIsSubmitting(false)
          return
        }
        recaptchaToken = await executeRecaptcha('contact_form_submit')
      }

      const result = await sendContactEmail({
        ...data,
        recipient,
        recaptchaToken,
      })

      if (result.success) {
        setIsSubmitted(true)
      } else {
        setErrors({ submit: result.error || 'Failed to submit form. Please try again.' })
        setIsSubmitting(false)
      }
    } catch {
      setErrors({ submit: 'Failed to submit form. Please try again.' })
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className={cn('w-full max-w-[434px] lg:max-w-[660px] mx-auto mt-2xl lg:mt-5xl', className)}
      {...(attr && { 'data-sanity': attr.toString() })}
    >
      {!isSubmitted && (contactForm.heading || contactForm.subheading) && (
        <div className="flex flex-col gap-sm mb-2xl lg:mb-5xl">
          <>
            {contactForm.heading && (
              <p className="size-subheading-lg font-bold text-neutral-900 lg:size-subheading-2xl">
                <PortableTextMinimal
                  value={contactForm.heading as PortableTextBlock[]}
                  tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
                />
              </p>
            )}
            {contactForm.subheading && (
              <p className="size-subheading-sm font-bold text-neutral-900 lg:size-subheading-lg">
                <PortableTextMinimal
                  value={contactForm.subheading as PortableTextBlock[]}
                  tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
                />
              </p>
            )}
          </>
        </div>
      )}

      {isSubmitted ? (
        <div className="w-full mx-auto max-w-[256px] md:max-w-[370px] lg:max-w-[660px] flex flex-col gap-md">
          <Icon
            provider="fontawesome"
            style="regular"
            name="faCheckCircle"
            size={40}
            className="text-primary-700 mx-auto"
          />
          <p className="size-subheading-lg lg:size-subheading-2xl font-bold text-center">
            Message sent
          </p>
          <p className="size-subheading-sm lg:size-subheading-lg font-bold text-neutral-900 text-center">
            {thankYouMessage}
          </p>
        </div>
      ) : (
        <form noValidate onSubmit={handleSubmit} className="flex flex-col gap-2xl lg:gap-5xl">
          <div className="flex flex-col gap-lg">
            <Input
              name="name"
              type="text"
              label="Name"
              required
              error={errors.name}
              disabled={isSubmitting}
              placeholder="John Doe"
            />

            <Input
              name="email"
              type="email"
              label="Email"
              required
              error={errors.email}
              disabled={isSubmitting}
              placeholder="john.doe@email.com"
            />

            <Input
              name="issueType"
              type="text"
              label="Issue type"
              required
              error={errors.issueType}
              disabled={isSubmitting}
              placeholder="E.g. connection, ads, account"
            />

            <Textarea
              name="message"
              label="Message"
              required
              error={errors.message}
              disabled={isSubmitting}
              rows={6}
              placeholder="Tell us how we can help"
            />
          </div>

          {errors.submit && (
            <p className="size-body-sm text-red-500 font-medium leading-normal">{errors.submit}</p>
          )}

          <div className="text-center">
            <Button
              button={{
                _type: 'button',
                variant: 'secondary',
                size: 'sm',
                link: undefined,
                icon: undefined,
                iconPlacement: 'none',
              }}
              className={cn(buttonSizeClasses({ lg: 'md' }), 'w-[208px] mx-auto')}
              tracking={tracking}
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          </div>
        </form>
      )}
    </div>
  )
}

/**
 * ContactForm component with reCAPTCHA provider.
 * This ensures reCAPTCHA script only loads on pages that contain a contact form,
 * rather than globally on all pages.
 */
export const ContactForm = (props: ContactFormProps) => {
  return (
    <ReCaptchaProvider>
      <ContactFormInner {...props} />
    </ReCaptchaProvider>
  )
}
